<script>
  import "../lib/styles/global.scss";
  import Cart from "../lib/components/utils/Cart.svelte";
  import Header from "../lib/components/layout/Header.svelte";
  import Footer from "../lib/components/layout/Footer.svelte";
  import { onMount } from "svelte";

  import { browser } from "$app/environment";
  import { afterNavigate } from "$app/navigation";
  import { navigating, page as pageState } from "$app/state";
  import { toStore } from "svelte/store";
  // SvelteKit 3 removes $app/stores. This layout is still legacy mode, where
  // $app/state alone is not reactive, so the two values its `$:` statements and
  // markup follow are bridged back into stores with Svelte's toStore. Kit
  // replaces page.url on every navigation, which is what the bridge reacts to
  // (hub ledger 2026-10-23-125; tests/app-state-reactivity.spec.js).
  const pageUrl = toStore(() => pageState.url);
  const navigatingTo = toStore(() => navigating.to);
  import { cartStore } from "#lib/stores/cart.js";
  import PageLoader from "../lib/components/ui-helpers/PageLoader.svelte";

  import { klaviyoStore } from "#lib/stores.js";
  import CookieBanner from "#lib/components/ui/CookieBanner.svelte";
  import CountrySelectionPopup from "#lib/components/ui/CountrySelectionPopup.svelte";
  import { injectSpeedInsights } from "@vercel/speed-insights";
  import { marketingConsentStore } from "#lib/stores.js";
  import {
    loadMarketingPixels,
    trackPixelPageView,
    setInternalVisitor,
  } from "#lib/utils/load-pixels.js";
  import { resolveConsentRegion } from "#lib/utils/consent-region.js";
  import { injectVercelAnalytics, trackVercelPageview } from "#lib/utils/vercel-analytics.js";
  import { get } from "svelte/store";
  import { registerABVariant, registerAttribution, upgradePostHog, captureEvent } from "#lib/utils/posthog.js";
  import {
    initRageClickDetector,
    initScrollDepthTracking,
    initSectionViewedTracking,
    initVideoEngagementTracking,
    initCartAbandonmentTracking,
    initOutboundLinkTracking,
  } from "#lib/utils/engagement.js";
  const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_PROJECT_API_KEY || '';
  import {
    showCountryPopupStore,
    detectedCountryStore,
    shouldShowCountryPopup,
    saveCountrySelection,
    dismissCountryPopup
  } from "#lib/stores/countryStore.js";
  import { handleCountryDetection, tryAutoAcceptCountry, reconcileCountrySignals } from "#lib/utils/geolocation.js";
  import StickyMobileCTA from "#lib/components/ui/StickyMobileCTA.svelte";
  import Signup10Modal from "#lib/components/ui/Signup10Modal.svelte";
  import { campaign } from "#lib/stores/campaign.js";
  import { activeCampaign, campaignById } from "#lib/campaign.js";


  let klaviyoLists = [];
  let vh; // Will only be set once
  let isFirefox = false;
  let cartId;
  let cartUrl;
  
  // Snow animation - load asynchronously after page is ready
  // let showSnow = false;
  // let snowflakes = [];

  function generateSnowflakes() {
    const isMobile = window.innerWidth < 768;
    const count = isMobile ? 50 : 100; // Fewer flakes on mobile
    
    return Array.from({ length: count }, (_, i) => {
      const rand = (min, max) => {
        const x = Math.sin(i * 9999 + min) * 43758.5453;
        const frac = x - Math.floor(x);
        return min + (max - min) * frac;
      };

      return {
        left: rand(0, 100),
        size: rand(2, 5),
        duration: rand(8, 16),
        delay: -rand(0, 16),
        swayAmount: rand(15, 40)
      };
    });
  }

  export let data;

  // Same answer as the server first, so hydration matches the HTML; the
  // visitor's clock decides on mount, which is what ends a campaign on a page
  // prerendered before its end date.
  campaign.set(campaignById(data.campaignId));
  onMount(() => campaign.set(activeCampaign()));

  // Everything the inline PostHog bootstrap in <svelte:head> needs, as one
  // JSON blob on a <meta>. The inline script is markup, not component code, so
  // it cannot read `data` or `import.meta.env` directly - a meta tag hands the
  // values over without {@html} or script-string concatenation (same shape as
  // SOLDR's `ab-bootstrap`).
  $: phBootstrapJson = JSON.stringify({
    apiKey: POSTHOG_API_KEY,
    distinctID: data?.distinctId || undefined,
    featureFlags: data?.featureFlags || undefined,
    abVariant: data?.abVariant || '',
    abTestId: data?.abTestId || '',
    environment: data?.environment || '',
    internal: data?.isInternal === true,
  });

  // Run tier-1 autoAccept synchronously at init, before reactive blocks
  // fire. If IP country === browser timezone country, this saves the
  // selection to localStorage so shouldShowCountryPopup() returns false
  // and the popup never even briefly flashes on first visit.
  //
  // If autoAccept doesn't fire, still seed detectedCountryStore synchronously
  // with the reconciled country so the popup preselection is correct the
  // moment it opens (otherwise the popup renders with its 'US' default,
  // then flips to the reconciled country once onMount resolves).
  if (browser) {
    const autoAccepted = tryAutoAcceptCountry(data?.country);
    if (!autoAccepted) {
      const reconciled = reconcileCountrySignals(data?.country);
      if (reconciled) {
        detectedCountryStore.set(reconciled);
      }
    }
  }

  // Tracking parameter preservation (UTM, affiliate, social media, etc.)
  let trackingParams = new URLSearchParams();

  // Seed synchronously from server-captured UTMs (hooks.server.js) so they are
  // available before any child component's onMount - in particular before the
  // cart drawer (Cart.svelte) eagerly creates a cart, which would otherwise be
  // stamped with empty attribution. Also survives ad blockers that stop the
  // client-side capture. captureUTMParams() re-applies the same URL-derived
  // values later, so this changes timing, not the resulting set.
  if (data?.trackingParams) {
    for (const [key, value] of Object.entries(data.trackingParams)) {
      if (value) trackingParams.set(key, value);
    }
  }

  function captureUTMParams() {
    const currentParams = $pageUrl.searchParams;
    const hasMarketingConsent = browser && localStorage.getItem("marketing") === "true";
    
    /* 
    ATTRIBUTION STRATEGY:
    
    1. AFFILIATE TRACKING (30 days, last-click wins)
       - Purpose: Commission tracking for affiliate payouts
       - Rule: Most recent affiliate link gets the commission
       - Example: bg_ref updates replace previous affiliate refs
    
    2. MARKETING CAMPAIGNS (30 days, first-click wins) 
       - Purpose: Campaign performance measurement
       - Rule: Original campaign gets attribution credit
       - Example: Facebook ad credited even if user later clicks Google retargeting
    
    3. PLATFORM IDs (7 days, last-click wins)
       - Purpose: Platform-specific attribution (FB, Google, etc.)
       - Rule: Most recent platform interaction tracked
       - Example: fbclid, gclid refresh with each click
    
    4. OTHER TRACKING (30 days, first-click wins)
       - Purpose: General referrer tracking
       - Rule: First referrer gets credit
    */
    
    // Attribution configuration with different rules for different parameter types
    const attributionConfig = {
      // Affiliate tracking (commission-based) - longer lifespan, last-click wins
      affiliate: {
        params: ['bg_ref', 'affiliate', 'aff', 'partner', 'sca_ref'],
        lifespanDays: 10,
        precedence: 'last-click'
      },
      
      // Marketing campaigns (attribution analysis) - first-click attribution
      marketing: {
        params: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'],
        lifespanDays: 30,
        precedence: 'first-click'
      },
      
      // Platform IDs (short-lived, last-click)
      platform: {
        params: ['fbclid', 'gclid', 'msclkid', 'ttclid'],
        lifespanDays: 7,
        precedence: 'last-click'
      },
      
      // Email and other tracking
      other: {
        params: ['mc_cid', 'mc_eid', 'ref', 'referrer', 'source', 'campaign', 'medium'],
        lifespanDays: 30,
        precedence: 'first-click'
      }
    };
    
    const now = Date.now();
    
    // Process each parameter type with its own rules
    Object.entries(attributionConfig).forEach(([type, config]) => {
      config.params.forEach(key => {
        if (currentParams.has(key)) {
          const newValue = currentParams.get(key);
          const expiryTime = now + (config.lifespanDays * 24 * 60 * 60 * 1000);
          
          // Check if we should update based on precedence rules
          const existingValue = hasMarketingConsent ? localStorage.getItem(`tracking_${key}`) : null;
          const existingExpiry = hasMarketingConsent ? localStorage.getItem(`tracking_${key}_expiry`) : null;
          
          let shouldUpdate = true;
          
          if (existingValue && existingExpiry) {
            // Check if existing value is still valid
            if (parseInt(existingExpiry) > now) {
              // Existing value is still valid
              if (config.precedence === 'first-click') {
                shouldUpdate = false; // Keep first value
              } else if (config.precedence === 'last-click') {
                // For last-click, only update if the value actually changed
                if (existingValue === newValue) {
                  shouldUpdate = false; // Same value, don't reset timer
                }
                // If value is different, we update (shouldUpdate stays true)
              }
            }
          }
          
          if (shouldUpdate) {
            trackingParams.set(key, newValue);
            if (hasMarketingConsent) {
              localStorage.setItem(`tracking_${key}`, newValue);
              localStorage.setItem(`tracking_${key}_expiry`, expiryTime.toString());
            }
          } else {
            // Use existing value
            trackingParams.set(key, existingValue);
          }
        }
      });
    });
  }

  function loadStoredUTMParams() {
    const hasMarketingConsent = browser && localStorage.getItem("marketing") === "true";
    if (hasMarketingConsent) {
      const allTrackingKeys = [
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
        'ref', 'referrer', 'source', 'bg_ref', 'affiliate', 'aff', 'partner', 'sca_ref',
        'fbclid', 'gclid', 'msclkid', 'ttclid', 'mc_cid', 'mc_eid', 'campaign', 'medium'
      ];
      
      const now = Date.now();
      
      allTrackingKeys.forEach(key => {
        const stored = localStorage.getItem(`tracking_${key}`);
        const expiry = localStorage.getItem(`tracking_${key}_expiry`);
        
        if (stored && expiry) {
          if (parseInt(expiry) > now) {
            // Still valid, load it
            trackingParams.set(key, stored);
          } else {
            // Expired, clean up
            localStorage.removeItem(`tracking_${key}`);
            localStorage.removeItem(`tracking_${key}_expiry`);
          }
        } else if (stored) {
          // Old format without expiry, keep for backward compatibility
          trackingParams.set(key, stored);
        }
      });
    }
  }

  function appendTrackingToLinks() {
    const hasMarketingConsent = browser && localStorage.getItem("marketing") === "true";
    
    // Only append tracking parameters with consent
    if (hasMarketingConsent && browser && trackingParams.size > 0) {
      // Get all internal links
      const links = document.querySelectorAll('a[href^="/"], a[href^="./"], a[href^="../"]');
      
      links.forEach(link => {
        try {
          const href = link.getAttribute('href');
          if (href) {
            const url = new URL(href, window.location.origin);
            
            // Add tracking parameters to the link
            trackingParams.forEach((value, key) => {
              if (!url.searchParams.has(key)) {
                url.searchParams.set(key, value);
              }
            });
            
            link.setAttribute('href', url.pathname + url.search);
          }
        } catch (e) {
          // Skip invalid URLs
        }
      });
    }
  }

  // Helper function to get current tracking parameters for programmatic navigation
  function getTrackingString() {
    return trackingParams.size > 0 ? '?' + trackingParams.toString() : '';
  }

  // Helper function for debugging tracking state
  function getTrackingDebugInfo() {
    if (!browser) return {};
    
    const debugInfo = {};
    const now = Date.now();
    
    Object.keys(localStorage)
      .filter(key => key.startsWith('tracking_') && !key.endsWith('_expiry'))
      .forEach(key => {
        const param = key.replace('tracking_', '');
        const value = localStorage.getItem(key);
        const expiry = localStorage.getItem(key + '_expiry');
        
        debugInfo[param] = {
          value,
          expiresAt: expiry ? new Date(parseInt(expiry)).toISOString() : 'No expiry',
          isValid: expiry ? parseInt(expiry) > now : true,
          daysUntilExpiry: expiry ? Math.ceil((parseInt(expiry) - now) / (24 * 60 * 60 * 1000)) : 'No expiry'
        };
      });
    
    return debugInfo;
  }

  // Make tracking functions available globally for other components (keeping UTM names for compatibility)
  if (browser) {
    window.getUTMParams = () => trackingParams;
    window.getUTMString = getTrackingString;
    window.debugTracking = getTrackingDebugInfo; // For debugging in console
  }

  // Global resource-load-error listener. Broken product/hero images, failed
  // video files, and dropped JS chunks / stylesheets all surface here in one
  // place - resource errors don't bubble, but a capture-phase window listener
  // catches them. Deduped so one persistently-broken asset can't spam PostHog.
  onMount(() => {
    if (!browser) return;
    const seen = new Set();
    const TRACKED = new Set(['IMG', 'VIDEO', 'SOURCE', 'SCRIPT', 'LINK']);
    function onResourceError(e) {
      const el = e?.target;
      const tag = el?.tagName;
      if (!tag || !TRACKED.has(tag)) return; // ignore JS runtime errors (no element target)
      const src = el.src || el.href || el.currentSrc || '';
      if (!src) return;
      const key = tag + '|' + src;
      if (seen.has(key)) return;
      seen.add(key);
      captureEvent('resource_load_error', {
        tag,
        src,
        path: window.location.pathname,
      });
    }
    window.addEventListener('error', onResourceError, true);
    return () => window.removeEventListener('error', onResourceError, true);
  });

  onMount(async () => {
    // Marketing pixels (Meta/TikTok/Reddit) - loads only with stored
    // consent (returning visitors). First-time accepts are covered by the
    // reactive consent subscription below.
    loadMarketingPixels();
  });

  // Internal (SNRGY team) visitors get no marketing pixels either - the
  // /internal switch used to suppress PostHog only. Set reactively (which runs
  // during init, before onMount) so it is in place before the first load call,
  // and re-runs if a navigation changes the answer.
  $: setInternalVisitor(data?.isInternal === true);

  // Load pixels the moment the visitor accepts marketing consent - without
  // this, the pixels would not load until the NEXT hard page load, so the
  // visitor's entire first session went untagged (no _fbp cookie → Meta
  // Website Custom Audiences never built). Idempotent, so the extra calls
  // after mount-time loading are no-ops.
  $: if (browser && $marketingConsentStore) {
    loadMarketingPixels();
  }



  // Re-check popup on every navigation (not just initial mount) so that
  // first-time visitors who land on a non-shop page still see the popup
  // when they navigate to the product page.
  $: if (browser && $pageUrl.pathname) {
    if (shouldShowCountryPopup()) {
      showCountryPopupStore.set(true);
    }
  }

  onMount(async () => {
    if (browser) {
      // Handle country detection and popup
      const detectedCountry = await handleCountryDetection(data.country);

      // Show popup if this is the first visit
      if (shouldShowCountryPopup()) {
        showCountryPopupStore.set(true);
      }

      // Initial vh for hero sections
      vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);

      // Get the real viewport height for scrolled content
      const realVh =
        Math.max(document.documentElement.clientHeight, window.innerHeight || 0) * 0.01;

      document.documentElement.style.setProperty("--vh-scroll", `${realVh}px`);

      const userAgent = window.navigator.userAgent.toLowerCase();
      isFirefox =
        userAgent.includes("firefox") ||
        userAgent.includes("fxios") || // Firefox for iOS
        typeof InstallTrigger !== "undefined"; // Firefox-specific object

      if (isFirefox) {
        document.documentElement.style.setProperty("--firefox-multiplier", `2`);
      } else {
        document.documentElement.style.setProperty("--firefox-multiplier", `1`);
      }

      // Tracking parameter handling
      loadStoredUTMParams(); // Load any previously stored tracking parameters
      captureUTMParams(); // Capture current tracking parameters
      
      // Append tracking parameters to links after DOM is ready
      setTimeout(appendTrackingToLinks, 100);

      // macOS bounce/elastic scroll is suppressed via CSS
      // `overscroll-behavior-y: none` (global.scss) - the old non-passive
      // document-level wheel listener forced every scroll event through JS
      // before the compositor could move, degrading scroll responsiveness
      // site-wide.

      // Load snow animation after page content is ready (async, non-blocking)
      // Use requestIdleCallback for better performance, fallback to setTimeout
      // const initSnow = () => {
      //   snowflakes = generateSnowflakes();
      //   showSnow = true;
      // };

      // if ('requestIdleCallback' in window) {
      //   requestIdleCallback(initSnow, { timeout: 2000 });
      // } else {
      //   setTimeout(initSnow, 1500);
      // }
    }

  });

  // Track previous URL to avoid unnecessary re-captures
  let previousUrl = '';
  let hasInitializedTracking = false;

  // All known tracking parameter names (used to detect and strip only these from the URL)
  const allTrackingParamNames = new Set([
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'bg_ref', 'affiliate', 'aff', 'partner', 'sca_ref',
    'fbclid', 'gclid', 'msclkid', 'ttclid',
    'mc_cid', 'mc_eid', 'ref', 'referrer', 'source', 'campaign', 'medium'
  ]);

  // Watch for page changes and reapply tracking parameters to new links
  $: if (browser && $pageUrl) {
    const currentUrl = $pageUrl.toString();
    
    // Only process if URL actually changed
    if (!hasInitializedTracking || currentUrl !== previousUrl) {
      // Check if any actual tracking params are present (not page/tag/sort etc.)
      const currentParams = $pageUrl.searchParams;
      const foundTrackingParams = [...currentParams.keys()].filter(k => allTrackingParamNames.has(k));
      
      // Capture and clean URL if there are actual tracking params
      if (foundTrackingParams.length > 0) {
        captureUTMParams();
        
        // Clean URL after capturing - only strip tracking params, preserve others (page, tag, sort, etc.)
        if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
          const cleanUrl = new URL($pageUrl);
          for (const key of foundTrackingParams) {
            cleanUrl.searchParams.delete(key);
          }
          const cleanHref = cleanUrl.pathname + (cleanUrl.search || '');
          window.history.replaceState({}, document.title, cleanHref);
        }
      }
      
      // Only append tracking to links if we have tracking params to append
      if (trackingParams.size > 0) {
        setTimeout(appendTrackingToLinks, 100);
      }
      
      hasInitializedTracking = true;
      previousUrl = currentUrl;
    }
  }

  

  // <main> used to carry in:fly={{ duration: 300, y: -30 }} inside the {#key},
  // so the page slid in on every cross-route navigation. With the key gone the
  // element persists and a Svelte transition would never replay, so the same
  // motion is played imperatively instead: same 300 ms, same -30px, and
  // cubicOut (fly's default easing) written out as a bezier. Kept on the same
  // condition the key had - a route-id change - so hash links and PDP-to-PDP
  // navigations stay unanimated exactly as before, and a hard load still gets
  // nothing (Svelte plays no intro on hydration).
  let mainEl;
  function playPageEnter() {
    if (typeof mainEl?.animate !== "function") return;
    mainEl.animate(
      [
        { opacity: 0, transform: "translateY(-30px)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 300, easing: "cubic-bezier(0.215, 0.61, 0.355, 1)" },
    );
  }

  // The section tracker only observes elements present at init time, so
  // re-scan after SPA navigations to pick up the new page's tagged sections.
  // (Video tracking uses document-level delegation - no re-scan needed.)
  // `from` is null on the initial hard load (already covered by onMount).
  afterNavigate(({ from, to }) => {
    // Vercel Web Analytics counts every navigation, the landing included, so
    // this sits above the `!from` return. Read the route from the page state,
    // not `to`: on the initial hydration Kit 2.70 hands afterNavigate a `to`
    // whose route id is null.
    trackVercelPageview(pageState);

    if (!browser || !from) return;

    if (from.route?.id !== to?.route?.id) playPageEnter();

    // Pixels only see the initial document load by themselves - fire a
    // page-view per SPA route change for whichever pixels are loaded.
    trackPixelPageView();

    // PostHog auto-captures its $pageview inside posthog.init(), i.e. once per
    // hard load - a client-side navigation produces none. Without this the
    // pageview count is really a visit count: no GLORB session has ever held a
    // second $pageview (1,485 one-pageview sessions in 7 days, none with two),
    // so pages/session is 1 by construction, bounce rate reads near 100% and
    // the path analysis has nothing to draw a path between. SOLDR captures it
    // by hand in the same handler; this matches.
    //
    // Hash-only navigations are skipped: afterNavigate also fires for in-page
    // anchors (#signup, #review-list, #product, the b2b page's #contact and
    // #sheet), and those are not pages. The pixel call above deliberately stays
    // ahead of this return so pixel behaviour is unchanged.
    //
    // Guarded on window.posthog: the SDK is loaded in onMount and is absent
    // without a key and for internal traffic.
    if (from.url.pathname !== to?.url?.pathname) {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('$pageview');
      }
    }

    if (!POSTHOG_API_KEY) return;
    setTimeout(() => {
      initSectionViewedTracking();
    }, 500);
  });

  onMount(() => {
    injectSpeedInsights();
    injectVercelAnalytics();

    // Expose the server-evaluated region consent requirement so the Meta CAPI
    // helper knows whether a server-side send is lawful without an explicit
    // consent click (opt-out regions like the US). Default to requiring consent.
    window.__REQUIRES_CONSENT = data?.requiresCookieConsent ?? true;
    // On prerendered routes the value above is a build-time default of `true`,
    // not a geo evaluation, so correct it once the server has answered.
    resolveConsentRegion(data?.requiresCookieConsent ?? true).then((needsConsent) => {
      window.__REQUIRES_CONSENT = needsConsent;
    });

    // --- PostHog analytics + A/B bootstrap ---
    // The SDK itself is already up: the inline script in <svelte:head> loads
    // and inits it before this module graph is even fetched, so a boot failure
    // is reported instead of taking the reporter with it (hub ledger
    // 2026-09-17-057). It also sets window.__FEATURE_FLAGS / __AB_VARIANT /
    // __AB_TEST_ID / __ENVIRONMENT, which is what registerABVariant() reads.
    //
    // What is left here is everything that only makes sense after hydration:
    // super-properties, attribution, the consent upgrade and the engagement
    // listeners. The gates repeat the inline script's so none of it runs when
    // there is no key, or when the visitor is internal (the /internal page sets
    // the glorb_internal cookie; the server track endpoint is separately gated
    // via locals.doNotTrack, and forced variants still render).
    let engagementCleanups = [];
    if (POSTHOG_API_KEY && !data?.isInternal) {
      registerABVariant();
      // Campaign attribution (utm_* + Meta placement/id) → super-props on every
      // event. Server-captured (data.trackingParams) so it survives ad blockers.
      registerAttribution(data?.trackingParams);

      // Upgrade to full persistence when marketing consent is granted.
      const unsubConsent = marketingConsentStore.subscribe((consent) => {
        if (consent) upgradePostHog();
      });

      // Engagement tracking - captureEvent no-ops if posthog absent, so safe.
      engagementCleanups = [
        initRageClickDetector(),
        initScrollDepthTracking(),
        initSectionViewedTracking(),
        initVideoEngagementTracking(),
        // Read the live cart store - the old cartItemsStore had no writers,
        // so cart_abandoned could never fire.
        initCartAbandonmentTracking(() => get(cartStore).items || []),
        initOutboundLinkTracking(),
        unsubConsent,
      ];
    }

    // Gorgias chat widget - only load on contact/support pages
    const loadGorgias = () => {
      if (!document.getElementById('gorgias-chat-widget-install-v3')) {
        const s = document.createElement('script');
        s.id = 'gorgias-chat-widget-install-v3';
        s.src = 'https://config.gorgias.chat/bundle-loader/01K09GJ1M1FSJK1TG4JEX76WRA';
        s.async = true;
        document.body.appendChild(s);
      }
    };
    const path = window.location.pathname;
    if (path.startsWith('/contact') || path.startsWith('/support')) {
      loadGorgias();
    }

    return () => {
      engagementCleanups.forEach((fn) => fn && fn());
    };
  });

  // Load Gorgias on client-side navigation to contact/support
  $: if (browser && $pageUrl.pathname) {
    const p = $pageUrl.pathname;
    if (p.startsWith('/contact') || p.startsWith('/support')) {
      if (!document.getElementById('gorgias-chat-widget-install-v3')) {
        const s = document.createElement('script');
        s.id = 'gorgias-chat-widget-install-v3';
        s.src = 'https://config.gorgias.chat/bundle-loader/01K09GJ1M1FSJK1TG4JEX76WRA';
        s.async = true;
        document.body.appendChild(s);
      }
    }
  }


  // Handle country selection from popup
  function handleCountrySelection(event) {
    const { country } = event.detail;
    saveCountrySelection(country, true); // true = manual selection
    // console.log('User selected country:', country);
  }

  // Listen for cart clearing due to region changes
  onMount(() => {
    if (browser) {
      window.addEventListener('countryRegionChanged', (event) => {
        const { from, to, fromCountry, toCountry } = event.detail;
        // console.log(`Layout: Cart cleared due to region change from ${from} (${fromCountry}) to ${to} (${toCountry})`);
        // You could show a toast notification here if desired
        // For now, we'll just log it
      });
    }
  });
</script>

<svelte:head>
  <!-- Font preload -->
  <link rel="preload" href="/fonts/NeueMontreal-Regular_1.woff2" as="font" type="font/woff2" crossorigin />
  <link rel="preload" href="/fonts/NeueMontreal-Light_1.woff2" as="font" type="font/woff2" crossorigin />
  <link rel="preload" href="/fonts/Whyte-Regular_1.woff2" as="font" type="font/woff2" crossorigin />
  <!-- <link rel="preload" as="video" href="/videos/hero-desktop_1.mp4" type="video/mp4" media="(min-width: 860px)" />
  <link rel="preload" as="video" href="/videos/firstpage-hero-mobile_1.mp4" type="video/mp4" media="(max-width: 859px)" /> -->
  <!--
    PostHog, inline and ahead of the module graph.

    This used to be `loadPostHog()` in $lib/utils/posthog.js, called from
    onMount. That put the reporter inside the module graph it reports on: a
    chunk that 404s after a deploy, a parse error on an older engine, a CSP
    block or a dropped connection on the first imports killed the page AND the
    only thing that could have told us (hub ledger 2026-09-17-057 - GLORB saw
    ~1 module-load $exception a day against 200-500 sessions where SOLDR, same
    SDK options and an inline init, saw 5-11 distinct visitors a day).

    Here it runs while the document parses, so the SDK's window.onerror is
    installed before anything can fail. Still cookieless: `persistence:
    'memory'` writes nothing to the device, so this needs no consent (R001 C4)
    - upgradePostHog() flips on persistence + session recording on Accept.

    The <meta> carries the server-evaluated bootstrap because this script is
    markup and cannot read `data` (see phBootstrapJson above).
  -->
  <meta name="ph-bootstrap" content={phBootstrapJson} />
  <script>
    (function () {
      var b = {};
      try {
        var m = document.querySelector('meta[name="ph-bootstrap"]');
        if (m) b = JSON.parse(m.getAttribute('content') || '{}') || {};
      } catch (e) { /* leave empty - never break the document for analytics */ }

      // Experiment state for registerABVariant() and the stamping below.
      // Published even when the SDK stays off, so hydrated code reads the same
      // values in every case.
      window.__FEATURE_FLAGS = b.featureFlags || {};
      window.__AB_VARIANT = b.abVariant || '';
      window.__AB_TEST_ID = b.abTestId || '';
      window.__ENVIRONMENT = b.environment || '';
      window.__PH_BOOTSTRAP = b;

      // Env-gated (no VITE_POSTHOG_PROJECT_API_KEY = no load), and never for a
      // visitor who marked themselves internal on /internal - no pageview, no
      // $feature_flag_called exposure, so SNRGY browsing cannot pollute
      // experiments or analytics.
      if (!b.apiKey || b.internal === true) return;
      // Init exactly once. __loaded only appears after array.js arrives, so it
      // cannot guard the window in between - and a second posthog.init() queued
      // on the stub would double every event once the real SDK replaces it.
      if (window.__PH_BOOTED || (window.posthog && window.posthog.__loaded)) return;
      window.__PH_BOOTED = true;

      // PostHog JS snippet - creates the window.posthog stub then loads
      // array.js from `${api_host}/static/array.js`, i.e. through our /ingest
      // proxy (ad-block resistant).
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

      var config = {
        api_host: '/ingest',
        ui_host: 'https://eu.posthog.com',
        persistence: 'memory',
        autocapture: false,
        // engagement.js runs our own rage_click detection; the built-in
        // $rageclick would double-count.
        rageclick: false,
        capture_pageview: true,
        capture_pageleave: true,
        // Autocapture unhandled JS exceptions into PostHog Error Tracking.
        // This is the option the whole move exists to make reachable at boot.
        capture_exceptions: true,
        disable_session_recording: true,
        // Stamp `environment` (+ legacy ab props) onto EVERY event at send
        // time. The initial $pageview fires inside posthog.init() - before any
        // hydrated code can register a super-property - so without this it goes
        // out untagged and lands as environment=none (~80% of $pageview did).
        // Registered super-props still win: never overwrite what is there.
        sanitize_properties: function (properties) {
          var out = {};
          for (var k in (properties || {})) out[k] = properties[k];
          if (window.__ENVIRONMENT && out.environment == null) out.environment = window.__ENVIRONMENT;
          if (window.__AB_VARIANT && out.ab_variant == null) out.ab_variant = window.__AB_VARIANT;
          if (window.__AB_TEST_ID && out.ab_test_id == null) out.ab_test_id = window.__AB_TEST_ID;
          return out;
        },
      };

      // Same identity and variant assignment the server evaluated, so the
      // client never re-rolls and events carry $feature/<key> from the first
      // pageview.
      if (b.distinctID || b.featureFlags) {
        config.bootstrap = {};
        if (b.distinctID) config.bootstrap.distinctID = b.distinctID;
        if (b.featureFlags) config.bootstrap.featureFlags = b.featureFlags;
      }

      posthog.init(b.apiKey, config);

      // Synthetic-probe tagging: the SNRGY uptime monitor appends
      // `?snrgy_probe` (or sets a snrgy_probe=1 cookie). Best-effort - never
      // break init for a monitor tag.
      try {
        if (/[?&]snrgy_probe(=|&|$)/.test(window.location.search || '')
          || /(?:^|;\s*)snrgy_probe=1(?:;|$)/.test(document.cookie || '')) {
          posthog.register({ synthetic: true });
        }
      } catch (e) { /* ignore - probe tagging is best-effort */ }

      // Returning visitor who already accepted marketing - upgrade here rather
      // than waiting for hydration, which is the same thing upgradePostHog()
      // does. A GPC opt-out never sets marketing=true, so those visitors stay
      // cookieless.
      try {
        if (localStorage.getItem('marketing') === 'true'
          && (typeof posthog.has_opted_in_capturing !== 'function' || !posthog.has_opted_in_capturing())) {
          posthog.opt_in_capturing();
          posthog.set_config({
            persistence: 'localStorage+cookie',
            disable_session_recording: false,
            autocapture: true,
            rageclick: false,
          });
        }
      } catch (e) { /* ignore - storage can throw in locked-down browsers */ }
    })();
  </script>

   <!-- Add these meta tags -->
   <meta name="robots" content="index, follow" />
   <meta name="author" content="GLORB" />
   <meta name="viewport" content="width=device-width, initial-scale=1.0" />
   
   <!-- Open Graph improvements (og:site_name is in lib/server/og-meta.js) -->
   <meta property="og:locale" content="en_US" />
   
   <!-- Twitter Card improvements -->
   <meta name="twitter:site" content="@glorb" />
   <meta name="twitter:creator" content="@glorb" />
  <meta
    name="keywords"
    content="smart lighting, LED lights, color changing lights, smart home, interior design, Glorb"
  />
  <!-- og:title/description/url/image/type/site_name and twitter:card/title/
       description/image are generated per page from its own <title> and
       description: lib/server/og-meta.js. The fallback description there
       mirrors the homepage <h1> in ImpactText.svelte - keep the two in step. -->


  <!-- Marketing pixels (Meta/TikTok/Reddit) load via loadMarketingPixels()
       in the layout script - on mount for returning consented visitors and
       reactively the moment consent is accepted (see load-pixels.js). -->
  <!-- No noscript Meta pixel here: it was gated on !data?.isInternal only, so
       it fired for every JS-disabled visitor regardless of marketing consent,
       region or sec-gpc - and nothing could stop it, because the consent
       machinery is JavaScript. The loader above is consent-gated and covers
       every visitor who can be asked in the first place. Hub ledger
       2026-09-17-058; SOLDR removed its copy for the same reason. -->
  <script>window.GORGIASCHAT_DISABLE_AUTO_OPEN = true;</script>

</svelte:head>

<Header />

<!-- add different pages if you want cart fetches to be done on other pages -->

  <Cart />

<!-- {#if showSnow}
  <div class="snow-layer" aria-hidden="true">
    {#each snowflakes as flake}
      <span
        class="snowflake"
        style={`left:${flake.left}%;width:${flake.size}px;height:${flake.size}px;animation-duration:${flake.duration}s;animation-delay:${flake.delay}s;--sway:${flake.swayAmount}px;`}
      ></span>
    {/each}
  </div>
{/if} -->


{#if $navigatingTo}
  <PageLoader />
{/if}

<!-- No {#key} around <main>: SvelteKit already swaps the page component on
     navigation. Keying on $page.route.id re-created the page a second time
     when the page store update landed after the swap, so every onMount side
     effect ran twice - product_viewed fired twice on a client-side navigation
     to the PDP (reproduced against www.glorb.me, 2026-09-10), and the Meta
     CAPI relay, the stock fetch and the section observers doubled with it.
     Hub ledger 2026-09-09-027, ported from SOLDR PR #189.

     The {#key} was also what replayed the page-enter transition, since a
     transition needs the element re-created. The animation now runs on a
     <main> that survives the navigation - see playPageEnter(). -->
<main bind:this={mainEl}>
  <slot />
</main>

<Footer />


<CookieBanner requiresCookieConsent={data.requiresCookieConsent ?? true} />

<CountrySelectionPopup
  show={$showCountryPopupStore}
  detectedCountry={$detectedCountryStore}
  on:save={handleCountrySelection}
  on:close={dismissCountryPopup}
/>

<StickyMobileCTA />
<Signup10Modal />

<style>
  main {
    min-height: 100vh;
    background-color: white;
  }

  /* .snow-layer {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1200;
    overflow: hidden;
  }

  .snowflake {
    position: absolute;
    top: -20px;
    background: rgba(255, 255, 255, 0.558);
    border-radius: 50%;
    opacity: 0.85;
    box-shadow: 0 0 3px rgba(255, 255, 255, 0.5);
    animation: snowfall linear infinite;
    will-change: transform;
  } */

  /* Remove blur on mobile for better performance */
  /* @media (min-width: 768px) {
    .snowflake {
      filter: blur(0.5px);
    }
  } */

  /* Disable animation for users who prefer reduced motion */
  /* @media (prefers-reduced-motion: reduce) {
    .snow-layer {
      display: none;
    }
  } */

  /* @keyframes snowfall {
    0% {
      transform: translateY(-20px) translateX(0);
    }
    25% {
      transform: translateY(25vh) translateX(var(--sway));
    }
    50% {
      transform: translateY(50vh) translateX(0);
    }
    75% {
      transform: translateY(75vh) translateX(calc(-1 * var(--sway)));
    }
    100% {
      transform: translateY(100vh) translateX(0);
    }
  } */
</style>
